import DOMPurify from 'isomorphic-dompurify'
import type { HeaderComponent, SavedDesign } from '../schemas/design.js'

/**
 * Sanitizes TextBlock/WatermarkBlock `content` HTML down to exactly what
 * the rich-text toolbar can produce. This matters more than typical stored
 * XSS: the server-side render target is Puppeteer — a real headless
 * browser with network egress — not a single victim's browser tab, so
 * unsanitized stored HTML is a stored-XSS-into-a-real-browser /
 * potential-SSRF risk, not just a client-side annoyance.
 *
 * Call this in three places (see the migration plan): client-side before
 * the save PUT request, server-side inside the preferences write handler
 * before persisting, and server-side again immediately before
 * renderDesignToHtml hands HTML to Puppeteer — that last call is the
 * load-bearing one and must run unconditionally even though it looks
 * redundant with the first two.
 */

const ALLOWED_TAGS = [
  'b',
  'i',
  'u',
  's',
  'strike',
  'span',
  'font',
  'br',
  'p',
  'div',
]
// `color`/`size`/`face` cover the <font> tags document.execCommand emits
// (foreColor/fontSize/fontName); `align` covers browsers that emit the
// legacy attribute for justifyLeft/Center/Right instead of a style.
const ALLOWED_ATTR = ['style', 'color', 'size', 'face', 'align']

// Only the inert, toolbar-producible properties may survive in a `style`
// attribute — no `position`, `background`, `content`, etc. — since this
// string is injected directly into a page rendered by a real browser
// (Puppeteer). `text-align` is how justifyLeft/Center/Right persists;
// `font-family` is how some browsers persist fontName. The weight/
// style/decoration trio is how toggle formatting lands on token chips
// (execCommand skips non-editable elements, so the editor mirrors
// bold/italic/underline/strike onto a styled wrapper instead).
const ALLOWED_STYLE_PROPERTIES = new Set([
  'color',
  'font-size',
  'font-family',
  'text-align',
  'font-weight',
  'font-style',
  'text-decoration',
])
const UNSAFE_STYLE_VALUE_PATTERN = /url\s*\(|expression\s*\(|javascript:/i

function sanitizeStyleAttributeValue(value: string): string {
  return value
    .split(';')
    .map(declaration => declaration.trim())
    .filter(Boolean)
    .filter(declaration => {
      const separatorIndex = declaration.indexOf(':')
      if (separatorIndex === -1) return false
      const property = declaration.slice(0, separatorIndex).trim().toLowerCase()
      const propertyValue = declaration.slice(separatorIndex + 1).trim()
      if (!ALLOWED_STYLE_PROPERTIES.has(property)) return false
      if (UNSAFE_STYLE_VALUE_PATTERN.test(propertyValue)) return false
      return true
    })
    .join('; ')
}

function restrictStyleAttribute(
  _node: Element,
  data: { attrName: string; attrValue: string }
): void {
  if (data.attrName === 'style') {
    data.attrValue = sanitizeStyleAttributeValue(data.attrValue)
  }
}

/**
 * Sanitizes a single HTML fragment (a TextBlock/WatermarkBlock `content`
 * value) to the allowlist above. `href`/`src` are never in ALLOWED_ATTR, so
 * anchors and embedded resources are stripped by construction rather than
 * by trying to allowlist "safe" URL schemes.
 */
export function sanitizeDesignHtml(html: string): string {
  DOMPurify.addHook('uponSanitizeAttribute', restrictStyleAttribute)
  try {
    return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute')
  }
}

function sanitizeComponent(component: HeaderComponent): HeaderComponent {
  if (component.type === 'text' || component.type === 'watermark') {
    return { ...component, content: sanitizeDesignHtml(component.content) }
  }
  return component
}

/**
 * Sanitizes every TextBlock/WatermarkBlock content field in a design,
 * leaving all other fields (geometry, colors, asset keys) untouched.
 */
export function sanitizeDesign(design: SavedDesign): SavedDesign {
  return {
    ...design,
    firstPage: {
      components: design.firstPage.components.map(sanitizeComponent),
    },
    subsequentPages: {
      components: design.subsequentPages.components.map(sanitizeComponent),
    },
  }
}
